import React, { useCallback, useEffect, useRef, useState } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { css } from '@codemirror/lang-css';
import { html } from '@codemirror/lang-html';
import { javascript } from '@codemirror/lang-javascript';
import { json } from '@codemirror/lang-json';
import { EditorView } from '@codemirror/view';
import { Menu, MenuItem } from '@progress/kendo-react-layout';
import { Popup } from '@progress/kendo-react-popup';
import prettier from 'prettier/standalone';
import * as parserHtml from 'prettier/parser-html';
import * as parserPostcss from 'prettier/parser-postcss';
import * as parserBabel from 'prettier/parser-babel';
import * as pluginEstree from 'prettier/plugins/estree';
import { AccentButton } from '../AccentButton';
import { contextMenuPopupProps, useContextMenuPopup } from '../unbound/useContextMenuPopup';

const safeString = (v) => (v ?? '').toString();
const safeItems = (v) => (Array.isArray(v) ? v : []);
const workflowSnippetMime = 'application/x-workflow-card-snippet';

export const AccentCodeEditor = ({
    language = 'css',
    defaultValue = '',
    onChange,
    onEditorCreated,
    height = '100%',
    width = '800px',
    onContextMenu,
    onFocus,
    templateCatalog,
    insertMode = 'token',
    onTemplateItemSelected,
    toolbarContent
}) => {
    const isAutoHeight = height === 'auto' || height === undefined || height === null;
    const [code, setCode] = useState(defaultValue);
    const { show: showContext, offset, menuRef, openAt, close: closeContext } = useContextMenuPopup();
    const editorViewRef = useRef(null);

    const hasTemplates = React.useMemo(() => {
        const list = Array.isArray(templateCatalog) ? templateCatalog : [];
        return list.some(g => (g?.items?.length ?? 0) > 0);
    }, [templateCatalog]);

    useEffect(() => {
        if (!showContext) return;

        const onMouseDown = (e) => {
            try {
                if (e?.target?.closest?.('.acc-template-menu')) return;
            } catch {
                // ignore
            }
            closeContext();
        };
        const onKeyDown = (e) => {
            if ((e?.key ?? '') === 'Escape') closeContext();
        };

        window.addEventListener('mousedown', onMouseDown, true);
        window.addEventListener('keydown', onKeyDown, true);
        return () => {
            window.removeEventListener('mousedown', onMouseDown, true);
            window.removeEventListener('keydown', onKeyDown, true);
        };
    }, [closeContext, showContext]);

    // Map language string to the appropriate language extension
    const getLanguageExtension = (lang) => {
        switch (lang.toLowerCase()) {
            case 'css':
                return css();
            case 'html':
                return html();
            case 'json':
                return json();
            case 'javascript':
                return javascript();
            default:
                throw new Error("Not supported language");
        }
    };

    // Handle editor change
    const handleChange = (value) => {
        setCode(value);
        if (onChange) onChange(value);
    };

    const insertSnippet = useCallback((snippet) => {
        const valueToInsert = safeString(snippet);
        if (!valueToInsert) return;

        const view = editorViewRef.current;
        if (!view) return;

        const selection = view.state.selection.main;
        view.dispatch({
            changes: { from: selection.from, to: selection.to, insert: valueToInsert },
            selection: { anchor: selection.from + valueToInsert.length },
            scrollIntoView: true
        });
        view.focus();
    }, []);

    const onTemplateSelect = useCallback(async (e) => {
        const data = e?.item?.data;
        const token = safeString(data?.token ?? data?.id);
        const snippet = safeString(data?.snippet ?? token);
        closeContext();
        const handled = await onTemplateItemSelected?.(data, {
            insertSnippet,
            insertMode
        });
        if (handled) return;
        insertSnippet(insertMode === 'snippet' ? (snippet || token) : token);
    }, [closeContext, insertMode, insertSnippet, onTemplateItemSelected]);

    const renderMenuItems = useCallback((items, parentKey) => {
        return safeItems(items).map((it, ii) => {
            const tok = safeString(it?.token).trim();
            const lbl = safeString(it?.label).trim();
            const snip = safeString(it?.snippet).trim();
            const children = safeItems(it?.items);

            if (tok) {
                const text = lbl || tok;
                return (
                    <MenuItem
                        key={`${parentKey}_leaf_${ii}_${tok}`}
                        text={text}
                        data={{ ...it, id: tok, token: tok, snippet: snip }}
                    />
                );
            }

            if (children.length > 0) {
                const text = lbl || 'Templates';
                return (
                    <MenuItem key={`${parentKey}_grp_${ii}_${text}`} text={text}>
                        {renderMenuItems(children, `${parentKey}_${ii}`)}
                    </MenuItem>
                );
            }

            return null;
        }).filter(Boolean);
    }, []);

    const dropExtension = React.useMemo(() => EditorView.domEventHandlers({
        drop(event, view) {
            const text = event?.dataTransfer?.getData?.(workflowSnippetMime) || event?.dataTransfer?.getData?.('text/plain');
            if (!text) return false;

            event.preventDefault();
            event.stopPropagation();

            const pos = view.posAtCoords({ x: event.clientX, y: event.clientY }) ?? view.state.selection.main.from;
            view.dispatch({
                changes: { from: pos, to: pos, insert: text },
                selection: { anchor: pos + text.length },
                scrollIntoView: true
            });
            view.focus();
            return true;
        }
    }), []);

    const [formatError, setFormatError] = useState('');

    // Format code using Prettier. The formatted text is pushed through onChange as
    // well as into local state: the host's value (what gets saved) must match what
    // the editor shows. Embedded <script>/<style> in HTML format via babel/postcss.
    const formatCode = async (e) => {
        try {
            setFormatError('');
            const codeString = typeof code === 'string' ? code : String(code);
            const lang = safeString(language).toLowerCase();
            const options = lang === 'html'
                ? { parser: 'html', plugins: [parserHtml, parserPostcss, parserBabel, pluginEstree] }
                : lang === 'css'
                    ? { parser: 'css', plugins: [parserPostcss] }
                    : lang === 'json'
                        ? { parser: 'json', plugins: [parserBabel, pluginEstree] }
                        : lang === 'javascript'
                            ? { parser: 'babel', plugins: [parserBabel, pluginEstree] }
                            : null;
            if (!options) return;

            const formatted = await prettier.format(codeString, options);
            if (typeof formatted !== 'string') return;
            setCode(formatted);
            if (onChange) onChange(formatted);
        } catch (err) {
            console.error('Error formatting code', err);
            // Prettier syntax errors carry the offending line; show the first line only.
            setFormatError(safeString(err?.message).split('\n')[0] || 'Unable to format the code.');
        } finally {
            e?.promise?.();
        }
    };



    return (
        <div
            className="accent-code-editor"
            style={{
                width,
                height: isAutoHeight ? 'auto' : height,
                border: '1px solid #ddd',
                borderRadius: '4px',
                overflow: isAutoHeight ? 'visible' : 'auto'
            }}
            onContextMenu={(e) => {
                onContextMenu?.(e);
                if (!hasTemplates) return;
                e.preventDefault();
                openAt(e);
            }}
            onFocus={onFocus}
        >
            {hasTemplates && (
                <Popup show={showContext} offset={offset} {...contextMenuPopupProps}>
                    <div ref={menuRef} style={{ display: 'inline-block' }}>
                        <Menu
                            className="acc-template-menu"
                            vertical
                            style={{ display: 'inline-block' }}
                            onSelect={onTemplateSelect}
                        >
                            {(templateCatalog ?? []).map((g, gi) => {
                                // a group may carry a translated label beside its (convention-keyed) name
                                const groupName = safeString(g?.label).trim() || safeString(g?.group) || 'Templates';
                                const items = safeItems(g?.items);
                                if (items.length === 0) return null;

                                return (
                                    <MenuItem key={`grp_${gi}_${safeString(g?.group) || groupName}`} text={groupName}>
                                        {renderMenuItems(items, `grp_${gi}`)}
                                    </MenuItem>
                                );
                            }).filter(Boolean)}
                        </Menu>
                    </div>
                </Popup>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 10 }}>
                <AccentButton onClick={formatCode}>
                    Format Code
                </AccentButton>
                {toolbarContent}
                {formatError && (
                    <span style={{ fontSize: 12, color: '#b00020', overflowWrap: 'anywhere' }}>{formatError}</span>
                )}
            </div>
            <CodeMirror
                value={code}
                height={isAutoHeight ? 'auto' : height}
                minHeight={isAutoHeight ? '200px' : undefined}
                width={width}
                theme="dark"
                extensions={[getLanguageExtension(language), dropExtension]}
                onChange={handleChange}
                onCreateEditor={(view) => {
                    editorViewRef.current = view;
                    if (onEditorCreated) onEditorCreated(view);
                }}
                basicSetup={{
                    lineNumbers: true,
                    highlightActiveLine: true,
                    highlightSelectionMatches: true,
                    autocompletion: true,
                    foldGutter: true,
                    dropCursor: true,
                    allowMultipleSelections: true,
                    indentOnInput: true,
                    bracketMatching: true,
                    closeBrackets: true
                }}
            />
        </div>
    );
};

export default AccentCodeEditor;
